import { Suspense, useEffect, useMemo, useRef } from "react";
import { lazyRoute } from "./staleChunkRecovery";
import { PageErrorBoundary } from "./PageErrorBoundary";
import { formatDateForApi } from "../dateFormatting";
import { useLanguage } from "../i18n/language-provider";
import type { PropertyAccessRole, UserRole, ViewerKind } from "../types/config";
import type { BookingHighlight } from "../services/recentBookingsApi";
import type { CalendarRangePreference, CalendarSelectRangeHandler } from "../hooks/useCalendarNavigation";
import type { FinalizeOnboardingResult } from "../services/onboardingApi";
import type { StepChangeOptions } from "./onboarding/OnboardingWizard";
import type { WizardPathContext } from "./onboarding/wizardSteps";
import { navigate, navigateUp } from "../navigation/history";
import { routePath, type AppRoute, type BookingStay } from "../navigation/routes";
import type { PageName } from "../navigation/pages";
import { useFrontDeskAccess } from "./access/read-only-access";
import { frontDeskPricesRoute } from "./access/front-desk-pages";
import type { UserFeature } from "../services/userFeatures";

const Settings = lazyRoute("Settings", async () => {
    const module = await import("./Settings");
    return { default: module.Settings };
});

const Strategies = lazyRoute("Strategies", async () => {
    const module = await import("./Strategies");
    return { default: module.Strategies };
});

const AccountSettings = lazyRoute("AccountSettings", async () => {
    const module = await import("./account/AccountSettings");
    return { default: module.AccountSettings };
});

const Drafts = lazyRoute("Drafts", async () => {
    const module = await import("./Drafts");
    return { default: module.Drafts };
});

const UserManagement = lazyRoute("UserManagement", async () => {
    const module = await import("./UserManagement");
    return { default: module.UserManagement };
});

const AdminPriceDrafts = lazyRoute("AdminPriceDrafts", async () => {
    const module = await import("./AdminPriceDrafts");
    return { default: module.AdminPriceDrafts };
});

const AdminPmsConnections = lazyRoute("AdminPmsConnections", async () => {
    const module = await import("./AdminPmsConnections");
    return { default: module.AdminPmsConnections };
});

const AdminWirePayments = lazyRoute("AdminWirePayments", async () => {
    const module = await import("./AdminWirePayments");
    return { default: module.AdminWirePayments };
});

const AdminStrategyHelp = lazyRoute("AdminStrategyHelp", async () => {
    const module = await import("./AdminStrategyHelp");
    return { default: module.AdminStrategyHelp };
});
const AdminActivityLog = lazyRoute("AdminActivityLog", async () => {
    const module = await import("./AdminActivityLog");
    return { default: module.AdminActivityLog };
});
const AdminUserFeatures = lazyRoute("AdminUserFeatures", async () => {
    const module = await import("./AdminUserFeatures");
    return { default: module.AdminUserFeatures };
});

const ConnectionManager = lazyRoute("ConnectionManager", async () => {
    const module = await import("./ConnectionManager");
    return { default: module.ConnectionManager };
});

const OnboardingWizard = lazyRoute("OnboardingWizard", async () => {
    const module = await import("./onboarding/OnboardingWizard");
    return { default: module.OnboardingWizard };
});

const RecentBookings = lazyRoute("RecentBookings", async () => {
    const module = await import("./RecentBookings");
    return { default: module.RecentBookings };
});

const Forecast = lazyRoute("Forecast", async () => {
    const module = await import("./Forecast");
    return { default: module.Forecast };
});

const DemandForecast = lazyRoute("DemandForecast", async () => {
    const module = await import("./DemandForecast");
    return { default: module.DemandForecast };
});

const Performance = lazyRoute("Performance", async () => {
    const module = await import("./Performance");
    return { default: module.Performance };
});

const SegmentChannelYield = lazyRoute("SegmentChannelYield", async () => {
    const module = await import("./SegmentChannelYield");
    return { default: module.SegmentChannelYield };
});

const MarketConditions = lazyRoute("MarketConditions", async () => {
    const module = await import("./MarketConditions");
    return { default: module.MarketConditions };
});

const StudyArchive = lazyRoute("StudyArchive", async () => {
    const module = await import("./study-archive/StudyArchive");
    return { default: module.StudyArchive };
});

const StudyArchiveEntry = lazyRoute("StudyArchiveEntry", async () => {
    const module = await import("./study-archive/StudyArchiveEntry");
    return { default: module.StudyArchiveEntry };
});

const Dashboard = lazyRoute("Dashboard", async () => {
    const module = await import("./Dashboard");
    return { default: module.Dashboard };
});

function PageLoadingFallback() {
    const { t } = useLanguage();

    return (
        <div className="flex min-h-[40vh] items-center justify-center px-6 py-10">
            <div className="flex flex-col items-center gap-3">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                <p className="text-sm text-muted-foreground">{t.common.loading}</p>
            </div>
        </div>
    );
}

/**
 * Wraps every page, so one boundary covers all of them. The boundary sits
 * outside Suspense because a rejected route import is thrown on the render
 * that would have shown the page — and each branch below is its own slot, so
 * navigating away mounts a fresh boundary rather than carrying the failure.
 * It is told which page it stands around, which is what its report names.
 */
function PageSuspense({ children, page }: { children: React.ReactNode; page: PageName }) {
    return (
        <PageErrorBoundary page={page}>
            <Suspense fallback={<PageLoadingFallback />}>{children}</Suspense>
        </PageErrorBoundary>
    );
}

interface PageRouterProps {
    /** The screen to show — already cleared by the shell's checks. */
    route: AppRoute;
    /** Move to another screen, through the same checks the rail goes through. */
    onNavigate: (route: AppRoute) => void;
    userRole: UserRole;
    /** The caller's access to the open property; null with none open. */
    activeConnectionRole: PropertyAccessRole | null;
    /** The open property; null with none open. */
    activeConnectionId: number | null;
    currentUserId: string | undefined;
    /** What an administrator switched on for the person: decides which account sections are offered. */
    userFeatures: readonly UserFeature[];
    /**
     * Whether the caller's access to the open property reaches its whole
     * account; false for a member given some of its properties, who is not
     * offered its payments. Undefined where the server said nothing.
     */
    accountWholeAccount?: boolean;
    /**
     * Read the person's features again: a section found its feature switched
     * off while it was open, or an administrator switched one for themselves
     * on the users console.
     */
    onFeaturesStale: () => void;
    // Calendar navigation
    fromDate: string;
    toDate: string;
    activeRangePreference?: CalendarRangePreference;
    isLoadingCalendarData: boolean;
    setIsLoadingCalendarData: (loading: boolean) => void;
    onDateChange: (direction: "prev" | "next") => void;
    onSelectRange: CalendarSelectRangeHandler;
    onJumpToToday: () => void;
    // Callbacks
    onCreditsChange: (newBalance: number) => void;
    onAnalysisCompleted: () => void;
    onOnboardingCompleted: (result?: FinalizeOnboardingResult) => void;
    onPmsConfigured: () => Promise<void>;
    onConnectionChanged: () => void;
    /**
     * The new-connection form has created a property: the caller's own, and
     * the open one now. Reported in place of `onConnectionChanged`.
     */
    onConnectionCreated: () => void;
    /**
     * The Connections page has opened a property it lists, and the caller's
     * access to it is the role its card names, and for a viewer's card the
     * kind of the grant too (`management` or `front_desk`) — or the admin
     * console has opened one an administrator added themselves to read-only,
     * as `staff`, which has no kind. Reported in place of `onConnectionChanged`.
     */
    onConnectionOpened: (role: PropertyAccessRole, viewerKind?: ViewerKind | null) => void;
    connectionVersion?: number;
    // Onboarding context
    pmsSetupRequired?: boolean;
    pmsHealthError?: string | null;
    onRedoWizard: () => void;
    onboardingStep: number;
    onOnboardingStepChange: (step: number, options?: StepChangeOptions) => void;
    onOnboardingHighestStepChange: (step: number) => void;
    /** Reports which onboarding steps the wizard will walk, for the sidebar rail. */
    onOnboardingSkipContextChange?: (context: WizardPathContext) => void;
    onboardingCompleted?: boolean;
}

/** A Recent Bookings booking, as the stay the Prices page's address carries. */
function stayOf(highlight: BookingHighlight): BookingStay {
    return {
        from: highlight.stayFrom,
        to: highlight.stayTo,
        roomKindId: highlight.roomKindId,
        canceled: highlight.isCanceled === true,
    };
}

export function PageRouter({
    route,
    onNavigate,
    userRole,
    activeConnectionRole,
    activeConnectionId,
    currentUserId,
    userFeatures,
    accountWholeAccount,
    onFeaturesStale,
    fromDate,
    toDate,
    activeRangePreference,
    isLoadingCalendarData,
    setIsLoadingCalendarData,
    onDateChange,
    onSelectRange,
    onJumpToToday,
    onCreditsChange,
    onAnalysisCompleted,
    onOnboardingCompleted,
    onPmsConfigured,
    onConnectionChanged,
    onConnectionCreated,
    onConnectionOpened,
    connectionVersion,
    pmsSetupRequired,
    pmsHealthError,
    onRedoWizard,
    onboardingStep,
    onOnboardingStepChange,
    onOnboardingHighestStepChange,
    onOnboardingSkipContextChange,
    onboardingCompleted = false,
}: PageRouterProps) {
    const draftsRoute = route.page === "drafts" ? route : null;
    const stayRoomKindId = draftsRoute?.stay?.roomKindId;
    const stayFrom = draftsRoute?.stay?.from;
    const stayTo = draftsRoute?.stay?.to;
    const stayCanceled = draftsRoute?.stay?.canceled === true;

    /**
     * Room-kind names the stays on the address were opened with. The address
     * carries no name, but the Recent Bookings click that framed the stay
     * knew it — so the banner names the room kind on the way in and on Back
     * and Forward to that visit, rather than reading "—".
     */
    const roomKindNames = useRef(new Map<string, string>());

    const openStay = (highlight: BookingHighlight, calendarView?: "reservations") => {
        roomKindNames.current.set(highlight.roomKindId, highlight.roomKindName);
        onNavigate({
            page: "drafts",
            tab: "calendar",
            stay: stayOf(highlight),
            ...(calendarView ? { calendarView } : {}),
        });
    };

    /**
     * The Recent Bookings highlight, rebuilt from the stay the address
     * carries — so Back and Forward return to it, and a reload keeps it. The
     * impact it shows is fetched per booking from these same four fields.
     */
    const bookingHighlight = useMemo<BookingHighlight | null>(
        () => (stayRoomKindId && stayFrom && stayTo
            ? {
                roomKindId: stayRoomKindId,
                roomKindName: roomKindNames.current.get(stayRoomKindId) ?? "",
                stayFrom,
                stayTo,
                isCanceled: stayCanceled,
                priceImpacts: [],
            }
            : null),
        [stayRoomKindId, stayFrom, stayTo, stayCanceled],
    );

    /**
     * The front desk's Prices page is the reservations grid alone, so an
     * address naming another of its tabs — a bookmark, a link a colleague
     * sent, the page open when the grant was narrowed — is corrected in place:
     * the page already draws the grid, and the address must not name a place
     * it is not showing. A correction replaces; Back still leaves the page.
     */
    const frontDesk = useFrontDeskAccess();
    const correctedPrices = frontDesk && draftsRoute ? frontDeskPricesRoute(draftsRoute) : null;
    const correctedPricesPath = correctedPrices ? routePath(correctedPrices) : null;
    useEffect(() => {
        if (correctedPricesPath) navigate(correctedPricesPath, { replace: true });
    }, [correctedPricesPath]);

    /**
     * Point the shared range at the stay whenever the address brings one.
     * `stayTo` is the checkout and therefore exclusive — the last priced night
     * is the day before. Not persisted: the range is borrowed for this visit,
     * and the user's own choice should survive leaving the page.
     */
    useEffect(() => {
        if (!stayFrom || !stayTo) return;
        const lastNight = new Date(`${stayTo}T00:00:00`);
        lastNight.setDate(lastNight.getDate() - 1);
        onSelectRange(stayFrom, formatDateForApi(lastNight), { persist: false });
    }, [stayFrom, stayTo, onSelectRange]);

    return (
        <>
            {route.page === "dashboard" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        {/* One property's outlook and figures, each read on
                            mount: another property opening starts it over. */}
                        <Dashboard key={connectionVersion} onNavigate={onNavigate} />
                    </PageSuspense>
                </div>
            )}

            {route.page === "strategies" && (
                <PageSuspense page={route.page}>
                    <Strategies
                        key={connectionVersion}
                        onCreditsChange={onCreditsChange}
                        onNavigateToConnections={() => onNavigate({ page: "connections" })}
                        onNavigateToDrafts={() => onNavigate({ page: "drafts", tab: "uploadLog" })}
                        onConnectionChanged={onConnectionChanged}
                    />
                </PageSuspense>
            )}

            {route.page === "recentBookings" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <RecentBookings
                            key={connectionVersion}
                            onNavigateToCalendar={(highlight) => openStay(highlight, "reservations")}
                            // No sub-view forced: the user's stored choice of
                            // price metric is the one they want to read.
                            onNavigateToDrafts={(highlight) => openStay(highlight)}
                        />
                    </PageSuspense>
                </div>
            )}

            {route.page === "forecast" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <Forecast key={connectionVersion} />
                    </PageSuspense>
                </div>
            )}

            {route.page === "demandForecast" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <DemandForecast key={connectionVersion} />
                    </PageSuspense>
                </div>
            )}

            {route.page === "performance" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <Performance key={connectionVersion} />
                    </PageSuspense>
                </div>
            )}

            {route.page === "segmentChannel" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <SegmentChannelYield key={connectionVersion} />
                    </PageSuspense>
                </div>
            )}

            {/* `key` matters more than usual here: both the assigned location
                and the competitor-tracking opt-in are per-connection, so
                switching must remount rather than show a neighbour's market. */}
            {route.page === "marketConditions" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <MarketConditions key={connectionVersion} />
                    </PageSuspense>
                </div>
            )}

            {/* Keyed like the other analytics: the list is the open
                property's, and an answer says whether it is for the property
                open, so a switch reads both again. */}
            {route.page === "studyArchive" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        {route.entryId ? (
                            <StudyArchiveEntry
                                key={`${connectionVersion}:${route.entryId}`}
                                entryId={route.entryId}
                                onBack={() => navigateUp(routePath({ page: "studyArchive" }), { fallback: "replace" })}
                                onNavigate={onNavigate}
                            />
                        ) : (
                            <StudyArchive key={connectionVersion} />
                        )}
                    </PageSuspense>
                </div>
            )}

            {draftsRoute && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <Drafts
                            key={connectionVersion}
                            fromDate={fromDate}
                            toDate={toDate}
                            activeRangePreference={activeRangePreference}
                            onSelectRange={onSelectRange}
                            onDateChange={onDateChange}
                            onJumpToToday={onJumpToToday}
                            isLoadingCalendarData={isLoadingCalendarData}
                            onCalendarDataLoadingChange={setIsLoadingCalendarData}
                            bookingHighlight={bookingHighlight}
                            // Dismissing ends the visit's borrowed context, so
                            // the address stops carrying it — in place, since
                            // Back should not bring a dismissed banner back.
                            onBookingHighlightDismiss={() =>
                                navigate(routePath({ page: "drafts", tab: draftsRoute.tab }), { replace: true })
                            }
                            initialViewMode={draftsRoute.tab ?? "calendar"}
                            // A tab is a place: picking one is a step Back returns from.
                            onViewModeChange={(tab) => onNavigate({ page: "drafts", tab })}
                            initialCalendarView={draftsRoute.calendarView}
                            // A requested view is applied once, on arrival; left in the
                            // address, a reload would overrule a view picked since.
                            onCalendarViewApplied={() =>
                                navigate(
                                    routePath({ page: "drafts", tab: draftsRoute.tab, stay: draftsRoute.stay }),
                                    { replace: true },
                                )
                            }
                            onManageRoomKinds={() => onNavigate({ page: "strategies" })}
                        />
                    </PageSuspense>
                </div>
            )}

            {route.page === "users" && userRole === "admin" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <UserManagement currentUserId={currentUserId} onOwnFeaturesChanged={onFeaturesStale} />
                    </PageSuspense>
                </div>
            )}

            {/* No key={connectionVersion}: the queue spans every tenant, so
                it must not reset when the active property switches. */}
            {route.page === "wirePaymentsAdmin" && userRole === "admin" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <AdminWirePayments />
                    </PageSuspense>
                </div>
            )}

            {/* No key={connectionVersion}: the page is tenant-global, like
                user management, and does not reset with the active property. */}
            {route.page === "priceDraftsAdmin" && userRole === "admin" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <AdminPriceDrafts />
                    </PageSuspense>
                </div>
            )}

            {/* No key={connectionVersion}: the console spans every tenant, so
                it must not reset when the active property switches. */}
            {route.page === "connectionsAdmin" && userRole === "admin" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <AdminPmsConnections
                            currentUserId={currentUserId}
                            // An administrator looks into a property from here
                            // read-only. Moved by the address, as the
                            // Connections page moves: the shell judges the
                            // move by the property open then, which a verdict
                            // asked at the click would not yet know.
                            onOpenProperty={() => {
                                onConnectionOpened("staff");
                                navigate(routePath({ page: "dashboard" }));
                            }}
                        />
                    </PageSuspense>
                </div>
            )}

            {/* No key={connectionVersion}: the log spans every user and
                every tenant, so it must not reset when the active property
                switches. */}
            {route.page === "activityAdmin" && userRole === "admin" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <AdminActivityLog />
                    </PageSuspense>
                </div>
            )}

            {/* No key={connectionVersion}: the queue spans every tenant, so it
                must not reset when the active property switches. */}
            {route.page === "strategyHelpAdmin" && userRole === "admin" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <AdminStrategyHelp />
                    </PageSuspense>
                </div>
            )}

            {/* The reminders' old address, which the e-mail a reminder sends on
                its day links to: the one strategy-help list, on "Follow-up due". */}
            {route.page === "supportRemindersAdmin" && userRole === "admin" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <AdminStrategyHelp initialScope="due" />
                    </PageSuspense>
                </div>
            )}

            {/* No key={connectionVersion}: the switches are people's, whichever
                property is open, so the page must not reset when it changes. */}
            {route.page === "featuresAdmin" && userRole === "admin" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <AdminUserFeatures currentUserId={currentUserId} onOwnFeaturesChanged={onFeaturesStale} />
                    </PageSuspense>
                </div>
            )}

            {/* Keyed on the open property: every card here reads and writes
                that property, and the sidebar switches property without
                leaving the page. Left mounted, the page kept showing the
                property it opened on, and saving wrote that form onto the one
                now active. Not keyed on `connectionVersion`, which this page's
                own save also moves: starting over then would drop the
                question the save has just asked — whether to re-run the
                wizard in the new currency. */}
            {route.page === "settings" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <Settings
                            key={activeConnectionId ?? "none"}
                            onPmsConfigured={onPmsConfigured}
                            onConnectionChanged={() => {
                                // A connection created from the new-connection
                                // form is the active one now, so the page is
                                // its settings and the address says so. It is
                                // the caller's own, which the shell hears
                                // first: somebody who had only a read-only
                                // property open would otherwise be refused the
                                // page the address is about to name.
                                if (route.newConnection) {
                                    onConnectionCreated();
                                    navigate(routePath({ page: "settings" }), { replace: true });
                                    return;
                                }
                                onConnectionChanged();
                            }}
                            createNewConnection={route.newConnection === true}
                            onNavigateToConnections={() =>
                                navigateUp(routePath({ page: "connections" }), { fallback: "replace" })
                            }
                            onRedoWizard={onRedoWizard}
                            // The rates are one table for every property; the
                            // server takes changes from an administrator only.
                            canManageExchangeRates={userRole === "admin"}
                            // How the open property connects is its account
                            // owner's to change; the server refuses anybody
                            // else. With no property open, saving connects
                            // one in the caller's own account.
                            canEditConnection={activeConnectionRole === null || activeConnectionRole === "owner"}
                            hasLighthouse={userFeatures.includes("lighthouse")}
                        />
                    </PageSuspense>
                </div>
            )}

            {/* No key={connectionVersion}: the account settings are the
                person's and the tenant's — the payments, the tokens, the AI
                agents — and must not reset when the active property switches.
                What the open property does decide is whether the payments are
                offered: not to somebody who only reads it, nor to a member
                given only some of its account's properties. What an
                administrator switched on decides whether the tokens and the AI
                agents are. */}
            {route.page === "account" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <AccountSettings
                            section={route.section}
                            readOnlyReason={
                                activeConnectionRole === "viewer" || activeConnectionRole === "staff"
                                    ? activeConnectionRole
                                    : null
                            }
                            features={userFeatures}
                            wholeAccount={accountWholeAccount}
                            onFeatureWithdrawn={onFeaturesStale}
                            // A section is a place: opening one is a step
                            // through the shell's checks, which Back returns from.
                            onSelectSection={(section) => onNavigate({ page: "account", section })}
                        />
                    </PageSuspense>
                </div>
            )}

            {route.page === "connections" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <ConnectionManager
                            onConnectionChanged={onConnectionChanged}
                            onConnectionOpened={onConnectionOpened}
                            // By the address, as the new-connection form moves:
                            // the page opens the property first, and the shell
                            // judges the move by the one open then. Asked at
                            // the click, it answered for the property open a
                            // moment earlier — a read-only one, perhaps.
                            onNavigateToSettings={() => navigate(routePath({ page: "settings" }))}
                            onAddNewConnection={() => onNavigate({ page: "settings", newConnection: true })}
                            onRedoWizard={onRedoWizard}
                            currentUserId={currentUserId}
                        />
                    </PageSuspense>
                </div>
            )}

            {route.page === "onboarding" && (
                <div className="flex-1 overflow-auto">
                    <PageSuspense page={route.page}>
                        <OnboardingWizard
                            key={connectionVersion}
                            onCompleted={onOnboardingCompleted}
                            onPmsConfigured={onPmsConfigured}
                            onConnectionChanged={onConnectionChanged}
                            onAnalysisCompleted={onAnalysisCompleted}
                            pmsSetupRequired={pmsSetupRequired}
                            pmsHealthError={pmsHealthError}
                            externalStep={onboardingStep}
                            requestedStepKey={route.step}
                            onStepChange={onOnboardingStepChange}
                            onHighestStepChange={onOnboardingHighestStepChange}
                            onSkipContextChange={onOnboardingSkipContextChange}
                            canExit={onboardingCompleted}
                            onExit={() => onNavigate({ page: "dashboard" })}
                        />
                    </PageSuspense>
                </div>
            )}
        </>
    );
}
